import { PlayerStatus } from '@tunein/web-player-manager';
import { api } from 'src/client/api';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { stores } from 'src/client/stores';
import config from 'src/common/config';
import { hidePopoutPlayerDialogCookie } from 'src/common/constants/cookies';
import {
  OPEN_IN_EMBED_DIALOG_FREQUENCY,
  OPEN_IN_EMBED_ENABLED,
} from 'src/common/constants/experiments/popout';
import { selectIsDiscord, selectTuneInUserSerial } from 'src/common/selectors/app';
import { selectExperiment } from 'src/common/selectors/config';
import { getCookie, setCookie } from 'src/common/utils/cookie';
import openStandardWindow from 'src/common/utils/window/openStandardWindow';
import { POPUP_PLAYER_READY, UPDATE_POPUP_PLAYER } from './constants/message';

const { insecureEmbedOrigin } = config;

const externalUrlPopOutDimensions = {
  width: 1180,
  height: 900,
};
const standardPopoutDimensions = {
  width: 640,
  height: 100,
};

export class PopoutPlayer {
  #popoutWindow = null;
  #guideId = null;
  #isPremium = null;
  #partnerId = '';
  #serial = '';
  #externalPlayerUrl = null;
  #player;

  get isEnabled() {
    const state = storeSingleton.state;
    const openInEmbedEnabled = selectExperiment(state, OPEN_IN_EMBED_ENABLED);
    const isDiscord = selectIsDiscord(state);

    return openInEmbedEnabled && !isDiscord;
  }

  async init(player) {
    this.#player = player;

    player.on('error', (event) => {
      this.#externalPlayerUrl = null;

      if (
        popoutPlayer.isEnabled &&
        event.error?.fatal &&
        event.isLastSource &&
        event.hadOnlyHttpStreams
      ) {
        popoutPlayer.promptUser();
      }
    });

    player.on('externalplayerurl', (url) => {
      this.#externalPlayerUrl = url;

      popoutPlayer.promptUser();
    });

    player.on('playing', () => {
      if (this.#isOpen) {
        this.#close();
      }
    });
  }

  promptUser() {
    const { setPopoutState, setPlayerStatus } = stores.playerStore.getState();

    setPlayerStatus(PlayerStatus.IDLE);

    if (getCookie(hidePopoutPlayerDialogCookie.name)) {
      setPopoutState({ showButton: true, isDialogOpen: false });
      return;
    }

    const frequency = api.getConfigSetting(OPEN_IN_EMBED_DIALOG_FREQUENCY, 1);

    setCookie(hidePopoutPlayerDialogCookie.name, frequency);

    setPopoutState({ showButton: true, isDialogOpen: true });
  }

  openOrUpdate() {
    const previousGuideId = this.#guideId;
    const { currentGuideItem } = stores.playerStore.getState();

    this.#guideId = currentGuideItem.playableGuideId;
    this.#isPremium = api.isPremium;
    this.#partnerId = api.partnerId;
    // TODO: consider exposing serial on WAC
    this.#serial = selectTuneInUserSerial(storeSingleton.state);

    if (this.#externalPlayerUrl) {
      openStandardWindow({
        url: this.#externalPlayerUrl,
        ...externalUrlPopOutDimensions,
      });
      return;
    }

    this.#isOpen ? this.#send(previousGuideId) : this.#open();
  }

  get #isOpen() {
    return Boolean(this.#popoutWindow && !this.#popoutWindow.closed);
  }

  get #embedUrl() {
    return `${insecureEmbedOrigin}/embed/player/${this.#guideId}?mode=popout&partnerId=${this.#partnerId}&serial=${this.#serial}`;
  }

  #focus() {
    this.#popoutWindow?.focus();
  }

  #handlePopoutPremiumStatusEvent = (event) => {
    const { data, origin } = event;

    const type = data?.type;
    const isValidMessage = type === POPUP_PLAYER_READY && origin === insecureEmbedOrigin;

    if (isValidMessage) {
      this.#send();
    }
  };

  #openWindow({ width, height }) {
    const { screenLeft, screenX, innerWidth } = window;

    const attributeEntries = Object.entries({
      width,
      height,
      top: 0,
      left: (screenLeft || screenX) + (innerWidth - width),
    });
    const attributes = attributeEntries.map((entry) => entry.join('=')).join(',');

    return window.open(this.#embedUrl, '_blank', attributes);
  }

  #open() {
    // this is used as a way to communicate the premium status once the popout window loads
    window.addEventListener('message', this.#handlePopoutPremiumStatusEvent);

    this.#popoutWindow = this.#openWindow(standardPopoutDimensions);
  }

  #send(previousGuideId) {
    this.#focus();

    if (previousGuideId && previousGuideId !== this.#guideId) {
      this.#popoutWindow?.location.replace(this.#embedUrl);
      return;
    }

    const payload = {
      type: UPDATE_POPUP_PLAYER,
      isPremium: this.#isPremium,
      prerollPlayed: this.#player.didPlayPreroll,
    };

    this.#popoutWindow?.postMessage(payload, insecureEmbedOrigin);
  }

  #close() {
    window.removeEventListener('message', this.#handlePopoutPremiumStatusEvent);
    this.#popoutWindow?.close();
  }
}

export const popoutPlayer = new PopoutPlayer();
